import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Play, RotateCcw, Home as HomeIcon, Users, Sprout, Infinity as Inf } from "lucide-react";
import { RevealLines, EASE } from "../site/motion";
import { KitchenPanel, GrowPanel } from "./KitchenPanel";
import { M } from "../../data/site";
import { DemoVideoModal } from "../site/DemoVideoModal";

const PILLARS = [
  { icon: HomeIcon, a: "Healthier", b: "homes" },
  { icon: Users, a: "Stronger", b: "families" },
  { icon: Sprout, a: "Brighter", b: "tomorrows" },
  { icon: Inf, a: "One companion", b: "for everyday life and what’s next" },
];

const Pillars = () => (
  <div className="relative z-10 border-t border-cyan-400/15 bg-ink/70 backdrop-blur-xl">
    <div className="mx-auto grid max-w-[1440px] grid-cols-2 gap-y-6 px-5 py-6 lg:grid-cols-4 lg:px-12">
      {PILLARS.map((p, i) => (
        <div key={p.a} className={`flex items-center gap-4 ${i ? "lg:border-l lg:border-cyan-400/20 lg:pl-8" : ""}`}>
          <p.icon className="h-8 w-8 shrink-0 text-cyan-200" strokeWidth={1.3} />
          <p className="font-mono text-xs uppercase leading-relaxed tracking-[0.2em] text-slate-200">{p.a}<br />{p.b}</p>
        </div>
      ))}
    </div>
  </div>
);

export const HomeHero = ({ play, onReplay }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const bx = useSpring(useTransform(mx, [-0.5, 0.5], [18, -18]), { stiffness: 60, damping: 20 });
  const by = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 60, damping: 20 });
  const onMove = (e) => { mx.set(e.clientX / window.innerWidth - 0.5); my.set(e.clientY / window.innerHeight - 0.5); };
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="home-hero" className="relative overflow-hidden">
      <motion.div style={{ y: bgY, x: bx, translateY: by }} initial={{ scale: 1.2 }} animate={play ? { scale: 1.08 } : {}} transition={{ duration: 2.4, ease: EASE }} className="absolute inset-0 hidden lg:block">
        <img src={M("hero.jpg")} alt="Honesti cooking alongside a young girl in a warm kitchen" className="h-full w-full object-cover object-[68%_center]" />
      </motion.div>
      <div className="absolute inset-0 hidden bg-gradient-to-r from-ink via-ink/80 via-35% to-transparent lg:block" />
      <div className="absolute inset-x-0 bottom-0 hidden h-64 bg-gradient-to-t from-ink to-transparent lg:block" />

      <div className="relative h-[58svh] min-h-[380px] lg:hidden">
        <img src={M("hero.jpg")} alt="Honesti cooking alongside a young girl" className="absolute inset-0 h-full w-full object-cover object-[74%_center]" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-ink/50" />
      </div>

      <div className="relative z-10 mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 -mt-28 lg:mt-0 lg:min-h-[100svh] lg:grid-cols-12 lg:px-12 lg:pb-14 lg:pt-36">
        <div className="lg:col-span-8">
          <motion.p initial={{ opacity: 0 }} animate={play ? { opacity: 1 } : {}} transition={{ duration: 1 }} className="eyebrow mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-cyan-300" /> Meet Honesti
          </motion.p>
          <RevealLines play={play} testId="hero-headline" className="font-display text-[2.9rem] font-bold leading-[0.98] tracking-tight text-white sm:text-6xl xl:text-[4.6rem]"
            lines={[{ text: "One companion." }, { text: "Endless possibilities.", className: "text-grad" }]} />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={play ? { opacity: 1, y: 0 } : {}} transition={{ duration: 1, ease: EASE, delay: 0.4 }} className="mt-7 max-w-xl text-base leading-relaxed text-slate-200 md:text-lg">
            HonestChoice AI helps you create a healthier home today and a brighter tomorrow — with Honesti, one trusted companion for everyday life and what’s next.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={play ? { opacity: 1, y: 0 } : {}} transition={{ duration: 1, ease: EASE, delay: 0.55 }} className="mt-9 flex flex-wrap items-center gap-4">
            <button onClick={() => setDemoOpen(true)} data-testid="watch-demo-btn" className="btn-primary"><span className="grid h-7 w-7 place-items-center rounded-full border border-white/70"><Play className="h-3 w-3 fill-white" /></span>Watch the Demo</button>
            <Link to="/waitlist" data-testid="hero-join-waitlist-btn" className="btn-ghost">Join Waitlist <ArrowRight className="h-4 w-4" /></Link>
            <button onClick={onReplay} data-testid="replay-intro-btn" className="flex items-center gap-2 text-sm text-slate-200 underline-offset-4 transition-colors hover:text-cyan-200 hover:underline"><RotateCcw className="h-4 w-4" /> Replay Introduction</button>
          </motion.div>
          <div className="mt-12 lg:mt-14 lg:max-w-[680px]"><KitchenPanel play={play} /></div>
        </div>
        <div className="lg:col-span-3 lg:col-start-10 lg:mt-16"><GrowPanel play={play} /></div>
      </div>
      <Pillars />
      <DemoVideoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </section>
  );
};
